import React from 'react';
import { components, OptionProps, SingleValueProps } from 'react-select';
import { PlayIcon } from '@rsos/assets/icons';
import { Text, Box } from '@rsos/components/capstone/base';
import { soundFileMapper } from '@rsos/constants';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import useIsIRP from '@rsos/utils/useIsIRP';
import { trackAudioTonePreviewed } from '../audioSettingsTrackings';

/** An entry in `soundFileMapper` — a selectable tone. */
type Sound = {
  name: string;
  displayName: string;
  path: string;
};

/** A `react-select` option wrapping a `Sound`. */
type SoundOption = {
  label: string;
  value: string;
  data: Sound;
};

const sounds = soundFileMapper as Sound[];

const SelectedRow = ({ data: sound }: { data: SoundOption }) => (
  <Box horizontal align="center" justify="space-between" pr={2}>
    <Text size="normal" color="primaryText">
      {sound.label}
    </Text>
  </Box>
);

const GenericRow = ({ data: sound }: { data: SoundOption }) => {
  const { irpVersion } = useIsIRP();
  const audioElement = new Audio(sound.data.path);

  const handleAudioPlay = (e: React.MouseEvent) => {
    e.nativeEvent.stopImmediatePropagation();
    e.stopPropagation();
    audioElement.play().catch((error: DOMException) => {
      // Swallow NotAllowedError (the browser blocks playback until the user has
      // interacted with the page); report anything else.
      if (error.name !== 'NotAllowedError') {
        captureExceptionWithScope(error, {
          extra: {
            audioName: sound.data.name,
            audioSrc: sound.data.path,
            errorMessage: error?.message,
          },
        });
      }
    });
    trackAudioTonePreviewed(irpVersion, sound.data.name);
  };

  return (
    <Box horizontal align="center" justify="space-between">
      <Text size="normal" color="primaryText">
        {sound.label}
      </Text>
      <Box
        horizontal
        color="secondaryText"
        align="center"
        flow={10}
        onClick={handleAudioPlay}
        cursor="pointer"
      >
        <PlayIcon width={14} height={14} color="secondaryLink" />
      </Box>
    </Box>
  );
};

const ValueComponent = (props: SingleValueProps<SoundOption>) => (
  <components.SingleValue {...props}>
    <SelectedRow {...props} />
  </components.SingleValue>
);

const OptionComponent = (props: OptionProps<SoundOption, false>) => (
  <components.Option {...props}>
    <GenericRow {...props} />
  </components.Option>
);

export const customComponents = {
  Option: OptionComponent,
  SingleValue: ValueComponent,
};

/**
 * The Select is themed through a `theme` prop (the styled-components theme) and
 * a `noGap` prop, neither of which is part of react-select's own types — so the
 * style state is described locally.
 */
type ControlStyleState = {
  isDisabled: boolean;
  isFocused: boolean;
  menuIsOpen: boolean;
  selectProps: {
    theme: {
      colors: {
        foreground: string;
        primaryBase: string;
        heavyLineOutline: string;
        primaryText: string;
      };
    };
    noGap?: boolean;
    menuIsOpen: boolean;
  };
};

export const customValueStyle = {
  control: (
    styles: React.CSSProperties,
    state: ControlStyleState,
  ): React.CSSProperties => ({
    ...styles,
    height: '45px',
    opacity: `${state.isDisabled ? 0.4 : 1}`,
    background: `${state.selectProps.theme.colors.foreground}`,
    borderColor: `${
      state.menuIsOpen || state.isFocused
        ? state.selectProps.theme.colors.primaryBase
        : state.selectProps.theme.colors.heavyLineOutline
    } !important`,
    borderRadius:
      state.selectProps.noGap && state.selectProps.menuIsOpen
        ? '5px 5px 0 0'
        : 5,
    boxShadow: 'none',
    color: state.selectProps.theme.colors.primaryText,
  }),
  menuList: (styles: React.CSSProperties): React.CSSProperties => ({
    ...styles,
    maxHeight: '120px',
  }),
};

export const buildOption = (sound: Sound): SoundOption => ({
  label: sound.displayName,
  value: sound.name,
  data: sound,
});

export const getFormattedSoundValue = (
  type: string,
  tone?: string,
): SoundOption | undefined => {
  const alertsDefaultSound = sounds.find(s => s.name === 'glassVibes');
  const callsDefaultSound = sounds.find(s => s.name === 'xyloToggle');
  const smsDefaultSound = sounds.find(s => s.name === 'tripleVibe');

  const defaultSoundByType: Record<string, SoundOption | undefined> = {
    calls: callsDefaultSound && buildOption(callsDefaultSound),
    alerts: alertsDefaultSound && buildOption(alertsDefaultSound),
    sms: smsDefaultSound && buildOption(smsDefaultSound),
    tt911: smsDefaultSound && buildOption(smsDefaultSound),
    interagency_chat: smsDefaultSound && buildOption(smsDefaultSound),
    default: alertsDefaultSound && buildOption(alertsDefaultSound),
  };

  if (tone) {
    const existingTone = sounds.find(s => s.name === tone);
    return existingTone ? buildOption(existingTone) : defaultSoundByType[type];
  }

  return defaultSoundByType[type] || defaultSoundByType.default;
};
